
import { useEffect, useMemo, useState } from "react";
import { Autocomplete, Option } from "components/fields/autoCompleteField";
import { useGetArticles } from "modules/article/api/src/article.api";
import { useGetAllDevis } from "modules/devis/api/src/devis.api";
import { CreateBonDeCommandePayload } from "modules/bonDeCommande/types/bonDeCommande.interface";

interface SelectDevisProps {
  formValues: CreateBonDeCommandePayload;
  setFormValues: (values: CreateBonDeCommandePayload) => void;
  setShowAcompteModal?: (showAcompteModal: boolean) => void;
  devisId?: string;
}

export const SelectDevis: React.FC<SelectDevisProps> = ({
  formValues,
  setFormValues,
  setShowAcompteModal,
  devisId,
}) => {
  const [searchValue, setSearchValue] = useState("");
  const { data: articlesData } = useGetArticles(undefined);
  const { data: devisData, refetch, isLoading } = useGetAllDevis(searchValue, 30);
  const devisList = useMemo(() => devisData?.pages.flatMap((page) => page.data) || [], [devisData]);

  useEffect(() => {
    refetch();
  }, [searchValue, refetch]);

  const options = devisList
    .filter((devis) => {
      const documentNumber = `${devis.documentNumber.number}`;
      return documentNumber.toLowerCase().includes(searchValue.toLowerCase());
    })
    .map((devis) => {
      const documentNumber = `${devis.documentNumber.number}`;
      return {
        id: devis._id,
        name: `${documentNumber}`,
      };
    });

  const handleOptionChange = (option: Option) => {
    const selectedDevis = devisList.find((d) => d._id === option.id);
    if (!selectedDevis) return;

    const allArticles = articlesData?.pages.flatMap((p) => p.data) || [];

    const updatedLineItems = selectedDevis.lineItems.map((item) => ({
      ...item,
      priceHT: allArticles.find((a) => a._id === item.articleId)?.priceHT || item.priceHT,
      totalTTC: item.totalTTC,
      totalHT: item.totalHT,
      description: item.description,
      quantity: item.quantity,
      reductionType: item.reductionType,
      reductionAmount: item.reductionAmount,
      vatAmount: item.vatAmount,
      total: item.totalTTC,
    }));

    setFormValues({
      ...formValues,
      devisId: selectedDevis._id,
      clientId: selectedDevis.clientId,
      dueDate: new Date(selectedDevis.dueDate),
      interveners: selectedDevis.interveners,
      lineItems: updatedLineItems,
      totalTTC: selectedDevis.totalTTC,
      totalHT: selectedDevis.totalHT,
      devisDocumentNumber: selectedDevis.documentNumber.number,
    });

    if (setShowAcompteModal) {
      setShowAcompteModal(true);
    }
  };

  useEffect(() => {
    if (devisId && devisList.length > 0 && !formValues.devisId) {
      const selectedDevis = devisList.find((d) => d._id === devisId);

      setFormValues({
        ...formValues,
        devisId: selectedDevis?._id,
        documentNumber: selectedDevis?.documentNumber.number,
        clientId: selectedDevis?.clientId,
        interveners: selectedDevis?.interveners,
        lineItems: selectedDevis?.lineItems.map((item) => ({
          ...item,
          total: item.totalTTC,
        })),
        totalTTC: selectedDevis?.totalTTC,
        totalHT: selectedDevis?.totalHT,
        devisDocumentNumber: selectedDevis?.documentNumber.number,
      });
      setSearchValue(
        devisList.find((d) => d._id === devisId)?.documentNumber.number || ""
      );
      setShowAcompteModal(true);
    }
  }, [devisId, devisList, formValues, setFormValues, setShowAcompteModal]);

  return (
    <Autocomplete
      options={options}
      onSelect={handleOptionChange}
      value={[formValues.devisId]}
      label="Sélectionner un devis"
      searchValue={searchValue}
      setSearchValue={setSearchValue}
      isLoading={isLoading}
    />
  );
};
